import { NgClass } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	effect,
	OnDestroy,
	output,
	untracked,
} from '@angular/core';
import { CloseModalSvgComponent } from '@uc/shared/svg';
import { Product, ProductEnum, RadioSelect } from '@uc/web/shared/data-models';
import {
	CourseFinderComponent,
	FilterClearingToggleComponent,
	RadioSelectComponent,
	StudyLevelSelectComponent,
	UcasInputComponent,
} from '@web/shared/ui';
import { Params } from '@angular/router';
import {
	getDegreeLevelType,
	getStudyModeType,
	getUniversityRegionType,
} from '../../../utils/course-search-payload.utils';
import { UNDERGRADUATE_DEGREE_LEVELS } from '../../../static/courses-filter.properties';
import { MAX_UCAS_TARIFF } from '@uc/web/shared/data-access';
import { sortQueryParamsByKey } from '../../../utils/query-params.utils';
import { CourseSearchFilterPill } from '../../../models/course-search-filter.models';
import { removeFilterPill } from '../../../data-access/+store/course-search.actions';
import { CourseSearchFilterPillsComponent } from '../course-search-filter-pills/course-search-filter-pills.component';
import { getRouteSegmentsAndQueryParams } from '../../../utils/route.utils';
import { RemovedFiltersInfoComponent } from '../../clearing/removed-filters-info/removed-filters-info.component';
import { CourseFilterBaseComponent } from '../course-search-base/course-filter-base.component';

@Component({
	selector: 'uc-clearing-course-search-filter',
	templateUrl:
		'./../course-search-filter-template/course-search-filter-template.component.html',
	imports: [
		NgClass,
		CloseModalSvgComponent,
		CourseFinderComponent,
		StudyLevelSelectComponent,
		FilterClearingToggleComponent,
		RadioSelectComponent,
		UcasInputComponent,
		CourseSearchFilterPillsComponent,
		RemovedFiltersInfoComponent,
	],

	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClearingCourseSearchFilterComponent
	extends CourseFilterBaseComponent
	implements OnDestroy
{
	studyLevelChange = output<string>();
	clearingEnabled = true;

	private _filterToBeRemovedEffect = effect(() => {
		const filterToBeRemoved = this.filterToBeRemoved();
		if (filterToBeRemoved) {
			untracked(() => {
				this.onRemovePill(filterToBeRemoved);
				this.store.dispatch(removeFilterPill({ pill: null }));
			});
		}
	});

	constructor() {
		super();
	}

	ngOnDestroy() {
		this._filterToBeRemovedEffect.destroy();
	}

	onClearingSelect(isClearingChecked: boolean) {
		if (!isClearingChecked)
			this.studyLevelChange.emit(UNDERGRADUATE_DEGREE_LEVELS[0].link);
	}

	onStudyLevel(studyLevel: Product) {
		this.resetDegree();
		this.studyLevelChange.emit(studyLevel);
	}

	onDegreeSelect(degree: RadioSelect) {
		const link = degree.link;
		const degreeLevel = getDegreeLevelType(degree.link);
		this.filterChange.emit({ degreeLevel });

		link !== ProductEnum.Clearing
			? this.updateQueryParams(`degree=${link}`)
			: this._removeQueryParam('degree');
	}

	onLocationSelect(location: RadioSelect) {
		const link = location.link;
		const region = getUniversityRegionType(link);
		this.filterChange.emit({ region });
		link !== ''
			? this.updateQueryParams(`location=${link}`)
			: this._removeQueryParam('location');
	}

	onQueryParamsUpdate(queryParam: RadioSelect) {
		const [, value] = queryParam.link.split('=');
		const studyMode = getStudyModeType(value);
		this.filterChange.emit({ studyMode });
		this.updateQueryParams(queryParam.link);
	}

	onRemovePill(pill: CourseSearchFilterPill) {
		switch (pill.filterType) {
			case 'DEGREE': {
				this.resetDegree();
				this.filterChange.emit({ degreeLevel: 'ALL_CLEARING' });
				this._removeQueryParam('degree');
				break;
			}
			case 'LOCATION':
				this.resetLocation();
				this.filterChange.emit({ region: null });
				this._removeQueryParam('location');
				break;
			case 'STUDY_MODE':
				this.resetStudyOption();
				this.filterChange.emit({ studyMode: null });
				this._removeQueryParam('mode');
				break;
			case 'UCAS_POINTS':
				this.filterChange.emit({ tariffMin: 0, tariffMax: MAX_UCAS_TARIFF });
				this._removeQueryParam('ucaspts');
				break;
		}
	}

	async resetFiltersAndNavigate() {
		this.resetDegree();
		this.resetLocation();
		this.resetStudyOption();

		const queryParams: Params = {};
		const subject = this.subject() ?? '';

		const isHECoSSubject =
			await this.hecosSubjectValidatorSrv.isHECoSSubject(subject);

		if (this.isUniCourseSearch()) {
			queryParams['uni'] = this.uniSlug();
		}

		if (!isHECoSSubject && subject) {
			queryParams['search'] = subject;
		}

		const segments = this.determineSegmentsOnFilterReset(isHECoSSubject, subject);

		this.filterReset.emit();
		this.router.navigate(segments, {
			queryParams: sortQueryParamsByKey(queryParams),
		});
	}

	private _removeQueryParam(key: string) {
		const { queryParams } = getRouteSegmentsAndQueryParams(this.router.url);
		if (queryParams[key]) {
			delete queryParams[key];
		}
		this.router.navigate([], { queryParams });
	}
}
